import type { Database, Json } from '@/integrations/supabase/types';

type PreferenceRow = Database['public']['Tables']['user_preferences']['Row'];
type PreferenceUpdate = Database['public']['Tables']['user_preferences']['Update'];
type JsonRecord = Record<string, Json | undefined>;

export type ThemePreference = 'light' | 'dark' | 'system';

export interface NotificationPreferences {
  email: boolean;
  inApp: boolean;
  sms: boolean;
  securityAlerts: boolean;
  businessUpdates: boolean;
  marketingEmails: boolean;
  loginNotifications: boolean;
}

export interface PrivacyPreferences {
  usageAnalytics: boolean;
  personalisation: boolean;
  directoryVisibility: boolean;
}

export interface UserPreferencesModel {
  theme: ThemePreference;
  language: string;
  timezone: string;
  dateFormat: string;
  notifications: NotificationPreferences;
  privacy: PrivacyPreferences;
}

export interface UserPreferencesPatch {
  theme?: ThemePreference;
  language?: string;
  timezone?: string;
  dateFormat?: string;
  notifications?: Partial<NotificationPreferences>;
  privacy?: Partial<PrivacyPreferences>;
}

export const DEFAULT_USER_PREFERENCES: UserPreferencesModel = {
  theme: 'system',
  language: 'en',
  timezone: 'Europe/London',
  dateFormat: 'DD/MM/YYYY',
  notifications: {
    email: true,
    inApp: true,
    sms: false,
    securityAlerts: true,
    businessUpdates: true,
    marketingEmails: false,
    loginNotifications: true,
  },
  privacy: {
    usageAnalytics: true,
    personalisation: true,
    directoryVisibility: true,
  },
};

const asRecord = (value: Json | null | undefined): JsonRecord => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as JsonRecord
    : {}
);

const readBoolean = (
  record: JsonRecord,
  keys: string[],
  fallback: boolean,
) => {
  const value = keys.map((key) => record[key]).find((candidate) => typeof candidate === 'boolean');
  return typeof value === 'boolean' ? value : fallback;
};

const readTheme = (value?: string | null): ThemePreference => (
  value === 'light' || value === 'dark' ? value : 'system'
);

export const readNotificationPreferences = (
  value: Json | null | undefined,
): NotificationPreferences => {
  const record = asRecord(value);

  return {
    email: readBoolean(record, ['email', 'email_notifications', 'emailNotifications'], true),
    inApp: readBoolean(record, ['in_app', 'push', 'inApp'], true),
    sms: readBoolean(record, ['sms', 'sms_notifications', 'smsNotifications'], false),
    securityAlerts: readBoolean(record, ['security_alerts', 'security', 'securityAlerts'], true),
    businessUpdates: readBoolean(record, ['business_updates', 'businessUpdates'], true),
    marketingEmails: readBoolean(record, ['marketing_emails', 'marketing', 'marketingEmails'], false),
    loginNotifications: readBoolean(record, ['login_notifications', 'loginNotifications'], true),
  };
};

export const readPrivacyPreferences = (
  value: Json | null | undefined,
): PrivacyPreferences => {
  const record = asRecord(value);
  const profileVisibility = record.profile_visibility;

  return {
    usageAnalytics: readBoolean(record, ['usage_analytics', 'activity_tracking'], true),
    personalisation: readBoolean(record, ['personalisation', 'personalization'], true),
    directoryVisibility: readBoolean(
      record,
      ['directory_visibility'],
      typeof profileVisibility === 'string' ? profileVisibility !== 'private' : true,
    ),
  };
};

export const parseUserPreferences = (
  row: Partial<PreferenceRow> | null | undefined,
): UserPreferencesModel => {
  const productPreferences = asRecord(row?.product_preferences);

  return {
    theme: readTheme(row?.theme),
    language: row?.language?.trim() || DEFAULT_USER_PREFERENCES.language,
    timezone: row?.timezone?.trim() || DEFAULT_USER_PREFERENCES.timezone,
    dateFormat: row?.date_format?.trim()
      || (typeof productPreferences.date_format === 'string' ? productPreferences.date_format : null)
      || DEFAULT_USER_PREFERENCES.dateFormat,
    notifications: readNotificationPreferences(row?.notifications),
    privacy: readPrivacyPreferences(row?.privacy_settings),
  };
};

const withoutLegacyKeys = (record: JsonRecord, keys: string[]) => {
  const next = { ...record };
  keys.forEach((key) => delete next[key]);
  return next;
};

export const mergeNotificationPreferences = (
  stored: Json | null | undefined,
  patch: Partial<NotificationPreferences>,
): Json => {
  const merged = { ...readNotificationPreferences(stored), ...patch };
  const preserved = withoutLegacyKeys(asRecord(stored), [
    'email_notifications',
    'emailNotifications',
    'push',
    'inApp',
    'sms_notifications',
    'smsNotifications',
    'security',
    'securityAlerts',
    'businessUpdates',
    'marketing',
    'marketingEmails',
    'loginNotifications',
  ]);

  return {
    ...preserved,
    email: merged.email,
    in_app: merged.inApp,
    sms: merged.sms,
    security_alerts: merged.securityAlerts,
    business_updates: merged.businessUpdates,
    marketing_emails: merged.marketingEmails,
    login_notifications: merged.loginNotifications,
  };
};

export const mergePrivacyPreferences = (
  stored: Json | null | undefined,
  patch: Partial<PrivacyPreferences>,
): Json => {
  const merged = { ...readPrivacyPreferences(stored), ...patch };
  const preserved = withoutLegacyKeys(asRecord(stored), [
    'activity_tracking',
    'personalization',
    'two_factor_enabled',
    'twoFactorEnabled',
  ]);

  return {
    ...preserved,
    usage_analytics: merged.usageAnalytics,
    personalisation: merged.personalisation,
    directory_visibility: merged.directoryVisibility,
  };
};

export const buildUserPreferencesUpdate = (
  row: Partial<PreferenceRow> | null | undefined,
  patch: UserPreferencesPatch,
): PreferenceUpdate => {
  const update: PreferenceUpdate = { updated_at: new Date().toISOString() };

  if (patch.theme !== undefined) update.theme = patch.theme;
  if (patch.language !== undefined) update.language = patch.language.trim();
  if (patch.timezone !== undefined) update.timezone = patch.timezone.trim();
  if (patch.notifications) {
    update.notifications = mergeNotificationPreferences(row?.notifications, patch.notifications);
  }
  if (patch.privacy) {
    update.privacy_settings = mergePrivacyPreferences(row?.privacy_settings, patch.privacy);
  }
  if (patch.dateFormat !== undefined) {
    update.date_format = patch.dateFormat;
  }

  return update;
};
